import {
  ExternalConditionType,
  LoanChecklistRuleDefinition,
  StandardConditionType,
} from 'src/services/loans/types';

export const STANDARD_CONDITION_TYPES: {
  value: Exclude<ExternalConditionType, 'enhanced'>;
  label: string;
}[] = [
  { value: 'underwriting', label: 'Underwriting' },
  { value: 'preliminary', label: 'Preliminary' },
  { value: 'post-closing', label: 'Post-Closing' },
];

export const getNewRuleConditionDefaults = (): Pick<
  LoanChecklistRuleDefinition,
  | 'externalEnhancedConditionPushOnFailure'
  | 'externalConditionPushOnFailure'
  | 'externalConditionType'
  | 'standardCondition'
> => ({
  externalEnhancedConditionPushOnFailure: false,
  externalConditionPushOnFailure: false,
});

export const getEnabledStandardConditionDefaults = (): Pick<
  LoanChecklistRuleDefinition,
  | 'externalConditionPushOnFailure'
  | 'externalConditionType'
  | 'standardCondition'
> => ({
  externalConditionPushOnFailure: true,
  externalConditionType: 'underwriting',
  standardCondition: {},
});

export const getEnabledEnhancedConditionDefaults = (): Pick<
  LoanChecklistRuleDefinition,
  'externalEnhancedConditionPushOnFailure' | 'source'
> => ({
  externalEnhancedConditionPushOnFailure: true,
  source: 'MOS',
});

export type LoanConditionPushMode =
  | 'none'
  | 'enhanced'
  | 'standard'
  | 'unknown';

type LoanConditionPushSource = {
  externalLoanId?: string | null;
  metadata?: unknown;
  externalLoanObject?: unknown;
};

const normalizeBooleanIndicator = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().toLowerCase();
  if (['true', 'y', 'yes', '1'].includes(normalized)) return true;
  if (['false', 'n', 'no', '0'].includes(normalized)) return false;
  return undefined;
};

const getRecordValue = (value: unknown, key: string): unknown => {
  if (!value || typeof value !== 'object') return undefined;
  return (value as Record<string, unknown>)[key];
};

const getNestedRecordValue = (value: unknown, path: string[]): unknown => {
  let current = value;
  for (const segment of path) {
    current = getRecordValue(current, segment);
    if (current === undefined) return undefined;
  }
  return current;
};

export const resolveLoanConditionPushMode = (
  loan: LoanConditionPushSource | undefined,
): LoanConditionPushMode => {
  if (!loan?.externalLoanId) return 'none';

  const candidates = [
    getNestedRecordValue(loan, [
      'externalLoanObject',
      'loan',
      'useEnhancedConditionIndicator',
    ]),
    getNestedRecordValue(loan, [
      'externalLoanObject',
      'useEnhancedConditionIndicator',
    ]),
    getNestedRecordValue(loan.metadata, [
      'loan',
      'useEnhancedConditionIndicator',
    ]),
    getRecordValue(loan.metadata, 'useEnhancedConditionIndicator'),
    getRecordValue(loan.metadata, 'ENHANCEDCOND.X1'),
  ];
  const indicator = candidates
    .map(normalizeBooleanIndicator)
    .find((value): value is boolean => value !== undefined);

  if (indicator === true) return 'enhanced';
  if (indicator === false) return 'standard';
  return 'unknown';
};

const CONDITION_PUSH_AVAILABILITY_TEXT: Record<LoanConditionPushMode, string> =
  {
    enhanced: 'Enhanced loan',
    standard: 'Standard loan',
    none: 'Not an Encompass loan',
    unknown: 'Loan condition mode unavailable',
  };

export const getConditionPushAvailabilityText = (
  mode: LoanConditionPushMode,
): string => CONDITION_PUSH_AVAILABILITY_TEXT[mode];

export const isStandardConditionRule = (
  definition: Pick<
    LoanChecklistRuleDefinition,
    'externalConditionPushOnFailure' | 'externalConditionType'
  >,
) =>
  Boolean(definition.externalConditionPushOnFailure) &&
  definition.externalConditionType !== 'enhanced';

export const withPendingStandardConditionRemoval = (
  standardCondition: LoanChecklistRuleDefinition['standardCondition'],
  previousConditionType: StandardConditionType | undefined,
  nextConditionType: StandardConditionType,
): LoanChecklistRuleDefinition['standardCondition'] => {
  if (!standardCondition) return {};

  const next = { ...standardCondition };
  const previousConditionId = next.conditionId;

  if (!previousConditionId) {
    const pendingRemoval = next.pendingRemoval;
    if (pendingRemoval?.conditionType === nextConditionType) {
      delete next.pendingRemoval;
      return {
        ...next,
        conditionId: pendingRemoval.conditionId,
      };
    }

    return next;
  }

  delete next.conditionId;

  if (previousConditionType && previousConditionType !== nextConditionType) {
    next.pendingRemoval = {
      conditionId: previousConditionId,
      conditionType: previousConditionType,
    };
    return next;
  }

  return {
    ...next,
    conditionId: previousConditionId,
  };
};
